/**
 * Node Hover Menu Component
 * Displays action buttons when hovering over a node
 */

import React from 'react';
import { useUIStore } from '@flow-studio/store';
import { PlugIcon } from '@bizfirst/common-react';

interface NodeHoverMenuProps {
  nodeId: string;
  onDelete?: (e: React.MouseEvent) => void;
  onRun: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
  onConfigureConnector: (e: React.MouseEvent) => void;
  onMore: (e: React.MouseEvent) => void;
  helpUrl?: string;
}

export const NodeHoverMenu: React.FC<NodeHoverMenuProps> = ({
  onRun,
  onConfigureConnector,
  onMore,
  helpUrl,
}) => {
  const { theme } = useUIStore();

  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    top: '-32px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '2px',
    background: theme === 'dark'
      ? 'rgba(15, 23, 42, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    border: theme === 'dark'
      ? '1px solid rgba(58, 140, 69, 0.3)'
      : '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '3px',
    boxShadow: theme === 'dark'
      ? '0 0 14px rgba(58, 140, 69, 0.3), 0 6px 20px rgba(0, 0, 0, 0.6)'
      : '0 3px 10px rgba(0, 0, 0, 0.12)',
    zIndex: 1000,
    pointerEvents: 'auto',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '3px 8px',
    background: theme === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : '#f9fafb',
    border: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid #e5e7eb',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    color: theme === 'dark' ? '#e2e8f0' : '#374151',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  };

  const hoverBg = theme === 'dark'
    ? 'rgba(58, 140, 69, 0.15)' // Cyan tint on hover
    : '#f3f4f6';

  const handleButtonClick = (e: React.MouseEvent, action: (e: React.MouseEvent) => void) => {
    e.stopPropagation();
    e.preventDefault();
    action(e);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent node selection on first click
  };

  return (
    <div style={menuStyle} className="node-hover-menu nodrag nopan">
      <button
        style={buttonStyle}
        onClick={(e) => handleButtonClick(e, onRun)}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f9fafb';
        }}
        title="Run this step"
      >
         Run
      </button>

      <button
        style={buttonStyle}
        onClick={(e) => handleButtonClick(e, onConfigureConnector)}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f9fafb';
        }}
        title="Configure Connector"
      >
        <PlugIcon size={14} /> Config
      </button>

      <button
        style={buttonStyle}
        // Marks this specific button (not the whole node) so MoreOptionsMenu's
        // outside-click handler can tell "the toggle was clicked" from "somewhere
        // else on the node was clicked" — see MoreOptionsMenu.tsx for why that split matters.
        data-more-options-trigger="true"
        onClick={(e) => handleButtonClick(e, onMore)}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f9fafb';
        }}
        title="More options"
      >
        ⋯
      </button>

      {helpUrl && (
        <a
          href={helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...buttonStyle, textDecoration: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = hoverBg;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f9fafb';
          }}
          title="Open documentation"
          onClick={(e) => e.stopPropagation()}
        >
          ?
        </a>
      )}
    </div>
  );
};
